import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "components/context/AuthContext";
import { authApi, organizationApi } from "api";
import { useGoogleLogin, useFacebookLogin } from "hooks";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook } from "react-icons/fa";
import AuthLayout from "components/auth/AuthLayout";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = (searchParams.get("invite") || "").trim();
  const { loginWithToken, auth } = useAuth();

  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [inviteBanner, setInviteBanner] = useState(null);

  useEffect(() => {
    if (auth?.token) {
      navigate(inviteToken ? "/home" : "/onboarding");
    }
  }, [auth?.token, navigate, inviteToken]);

  useEffect(() => {
    if (!inviteToken) {
      setInviteBanner(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await organizationApi.getInviteInfo(inviteToken);
        if (!cancelled && data?.organization_name) {
          setInviteBanner({
            organizationName: data.organization_name,
            email: data.email || "",
          });
          setForm((prev) => ({
            ...prev,
            email: (prev.email || data.email || "").trim() || prev.email,
          }));
        }
      } catch {
        if (!cancelled) {
          setInviteBanner({ error: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const { startGoogleOAuthLogin, loading: googleLoading, error: googleError } = useGoogleLogin();
  const { startFacebookLogin, loading: facebookLoading, error: facebookError } = useFacebookLogin();

  const isLoading = emailLoading || googleLoading || facebookLoading;
  const globalError = error || googleError || facebookError;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailLoading(true);

    const { email, password, confirmPassword } = form;

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      setEmailLoading(false);
      return;
    }

    try {
      const payload = { email, password };
      if (inviteToken) {
        payload.invite_token = inviteToken;
      }
      await authApi.register(payload);
      const loginRes = await authApi.login(email, password);
      await loginWithToken(loginRes.data.access_token);
      navigate(inviteToken ? "/home" : "/onboarding");
    } catch (err) {
      const msg = err.response?.data?.error || "Error en el registro";
      setError(msg);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-center">
            <img className="mx-auto h-20 w-auto" src="/ch3.png" alt="Logo de la empresa" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Crea tu cuenta</h2>
          </div>

          {globalError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm font-medium text-red-800">{globalError}</div>
            </div>
          )}

          {inviteToken && inviteBanner?.organizationName && (
            <div className="mt-4 rounded-md bg-indigo-50 border border-indigo-100 p-4 text-left">
              <p className="text-sm text-indigo-900">
                Te invitaron a unirte a{" "}
                <span className="font-semibold">{inviteBanner.organizationName}</span> en espor.ai.
                {inviteBanner.email ? (
                  <>
                    {" "}
                    Registrate con el email <span className="font-mono text-xs">{inviteBanner.email}</span>.
                  </>
                ) : null}
              </p>
            </div>
          )}
          {inviteToken && inviteBanner?.error && (
            <div className="mt-4 rounded-md bg-amber-50 p-4 text-sm text-amber-900">
              El enlace de invitación no es válido o expiró. Podés registrarte sin invitación o pedir un nuevo enlace.
            </div>
          )}

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <input
                name="email"
                type="email"
                placeholder="Correo electrónico"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-t-md text-sm"
                disabled={isLoading || (Boolean(inviteBanner?.email) && Boolean(inviteToken))}
              />
              <input
                name="password"
                type="password"
                placeholder="Contraseña"
                value={form.password}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 text-sm"
                disabled={isLoading}
              />
              <input
                name="confirmPassword"
                type="password"
                placeholder="Confirmar contraseña"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-b-md text-sm"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
              disabled={isLoading}
            >
              {emailLoading ? "Creando cuenta..." : "Registrarse"}
            </button>
          </form>

          {!inviteToken && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">O continúa con</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={startGoogleOAuthLogin}
                className="w-full flex items-center justify-center px-8 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                disabled={googleLoading}
              >
                <FcGoogle className="h-5 w-5 mr-2" />
                Google
              </button>
              <button
                type="button"
                onClick={startFacebookLogin}
                className="w-full flex items-center justify-center px-8 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                disabled={facebookLoading}
              >
                <FaFacebook className="h-5 w-5 mr-2 text-blue-600" />
                Facebook
              </button>
            </div>

            <p className="mt-6 text-sm text-center text-gray-600">
              ¿Ya tienes una cuenta?{" "}
              <a href="/login" className="text-indigo-600 hover:underline">
                Inicia sesión aquí
              </a>
            </p>
          </div>
          )}
    </AuthLayout>
  );
};

export default Register;
